// ============================================
// Settings Page
// ============================================

import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../utils/api';
import { Loader } from '../components/Loader';
import { cn } from '../components/ui';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Save,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

export function SettingsPage() {
  const { admin, fetchMe } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');

  // Profile form state
  const [fullName, setFullName] = useState(admin?.full_name || '');
  const [email, setEmail] = useState(admin?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Security form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [securitySuccess, setSecuritySuccess] = useState(false);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);

    try {
      await authApi.updateMe({ full_name: fullName });
      setProfileSuccess(true);
      await fetchMe();
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err: any) {
      setProfileError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSecuritySave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecuritySaving(true);
    setSecurityError(null);
    setSecuritySuccess(false);

    if (newPassword !== confirmPassword) {
      setSecurityError('New passwords do not match');
      setSecuritySaving(false);
      return;
    }

    if (newPassword.length < 8) {
      setSecurityError('Password must be at least 8 characters');
      setSecuritySaving(false);
      return;
    }

    try {
      // Note: This would need a backend endpoint for password change
      // For now, show not implemented
      alert('Password change endpoint needs to be implemented in backend');
      // await authApi.changePassword({ current_password: currentPassword, new_password: newPassword });
      setSecuritySuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSecuritySuccess(false), 3000);
    } catch (err: any) {
      setSecurityError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSecuritySaving(false);
    }
  };

  const PasswordInput = ({
    label,
    value,
    onChange,
    showPassword,
    onToggleShow,
    placeholder,
    disabled,
    error,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    showPassword: boolean;
    onToggleShow: () => void;
    placeholder: string;
    disabled?: boolean;
    error?: string;
  }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-12 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={!!error}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account preferences</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-1" role="tablist">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'security', label: 'Security', icon: Lock },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => setActiveTab(id as typeof activeTab)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                activeTab === id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <form onSubmit={handleProfileSave} className="bg-card border border-border rounded-xl p-6 space-y-6">
          <h2 className="text-lg font-semibold">Profile Information</h2>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled
                />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Email cannot be changed</p>
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium mb-1">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  id="full_name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="John Doe"
                />
              </div>
            </div>
          </div>

          {/* Role info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-medium mb-2">Account Details</h3>
            <dl className="grid gap-2 md:grid-cols-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Role</dt>
                <dd className="font-medium capitalize">{admin?.is_superuser ? 'Super Admin' : 'Admin'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd className="font-medium">{admin?.is_active ? 'Active' : 'Inactive'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Member since</dt>
                <dd className="font-medium">{admin?.created_at ? new Date(admin.created_at).toLocaleDateString() : 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last login</dt>
                <dd className="font-medium">{admin?.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'}</dd>
              </div>
            </dl>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={profileSaving}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
            >
              {profileSaving ? (
                <>
                  <Loader size="sm" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>

          {profileError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{profileError}</p>
            </div>
          )}

          {profileSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">Profile updated successfully</p>
            </div>
          )}
        </form>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <form onSubmit={handleSecuritySave} className="bg-card border border-border rounded-xl p-6 space-y-6">
          <h2 className="text-lg font-semibold">Change Password</h2>
          <p className="text-muted-foreground">Your password must be at least 8 characters long.</p>

          <div className="space-y-4">
            <PasswordInput
              label="Current Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              showPassword={showCurrentPassword}
              onToggleShow={() => setShowCurrentPassword(!showCurrentPassword)}
              placeholder="••••••••"
              disabled={securitySaving}
            />

            <PasswordInput
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              showPassword={showNewPassword}
              onToggleShow={() => setShowNewPassword(!showNewPassword)}
              placeholder="••••••••"
              disabled={securitySaving}
            />

            <PasswordInput
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              showPassword={showConfirmPassword}
              onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
              placeholder="••••••••"
              disabled={securitySaving}
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={securitySaving}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
            >
              {securitySaving ? (
                <>
                  <Loader size="sm" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </div>

          {securityError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{securityError}</p>
            </div>
          )}

          {securitySuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">Password updated successfully</p>
            </div>
          )}

          {/* API Keys section (placeholder) */}
          <div className="border-t border-border pt-6">
            <h2 className="text-lg font-semibold mb-4">API Access</h2>
            <p className="text-muted-foreground mb-4">
              API key management for programmatic access will be available here.
            </p>
            <button className="px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm">
              Generate API Key (Coming Soon)
            </button>
          </div>
        </form>
      )}
    </div>
  );
}